import { useCallback, useEffect, useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { SkeletonPosSalesHistory, SkeletonPaginationRow, SkeletonTable } from '@/components/ui/Skeleton'
import { formatMoney } from '@/lib/money'
import { useCurrentStore } from '@/context/CurrentStoreContext'
import { getUserFacingErrorMessage } from '@/utils/userFacingError'
import * as posApi from './posApi'

function isoDateLocal(d: Date) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default function SalesHistoryPage() {
  const { currentStoreId: storeId, storeRecord, loading: storeContextLoading } = useCurrentStore()
  const posEnabled = Boolean(storeRecord?.posEnabled)
  const [listLoading, setListLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [from, setFrom] = useState(() => isoDateLocal(new Date()))
  const [to, setTo] = useState(() => isoDateLocal(new Date()))
  const [page, setPage] = useState(1)
  const [data, setData] = useState<Awaited<ReturnType<typeof posApi.listPosSales>> | null>(null)

  const loadSales = useCallback(async () => {
    if (storeId == null) return
    setListLoading(true)
    setError(null)
    try {
      const fromIso = new Date(from + 'T00:00:00.000Z').toISOString()
      const toIso = new Date(to + 'T23:59:59.999Z').toISOString()
      const res = await posApi.listPosSales(storeId, {
        page,
        limit: 20,
        from: fromIso,
        to: toIso,
      })
      setData(res)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Failed to load sales'))
      setData(null)
    } finally {
      setListLoading(false)
    }
  }, [storeId, from, to, page])

  useEffect(() => {
    setPage(1)
  }, [storeId])

  useEffect(() => {
    void loadSales()
  }, [loadSales])

  if (storeContextLoading) {
    return <SkeletonPosSalesHistory />
  }

  if (!posEnabled || storeId == null) {
    return (
      <Card>
        <CardHeader
          title="POS unavailable"
          description="In-store POS is not enabled for your store."
        />
      </Card>
    )
  }

  const currency = storeRecord?.currency ?? 'USD'

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title="Sales history" description="Filter by date range (UTC day boundaries)." />
        <div className="mb-6 flex flex-wrap items-end gap-4">
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">From</span>
            <Input
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value)
                setPage(1)
              }}
            />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">To</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value)
                setPage(1)
              }}
            />
          </label>
        </div>
        {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}
        {listLoading ? (
          <div aria-busy="true" aria-label="Loading sales">
            <SkeletonTable rows={8} columns={6} />
            <SkeletonPaginationRow />
          </div>
        ) : data && data.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No sales in this range.</p>
        ) : data ? (
          <>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="border-b border-border bg-muted/40 text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">S/N</th>
                    <th className="px-3 py-2">Reference</th>
                    <th className="px-3 py-2">Time</th>
                    <th className="px-3 py-2">Staff</th>
                    <th className="px-3 py-2">Payment</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row, rowIndex) => (
                    <tr key={row.id} className="border-b border-border/80">
                      <td className="px-3 py-2 tabular-nums">{(data.page - 1) * data.limit + rowIndex + 1}</td>
                      <td className="px-3 py-2 font-mono text-xs">{row.saleReference}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {new Date(row.createdAt).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        {row.staff
                          ? `${row.staff.firstName} ${row.staff.lastName}`.trim()
                          : '—'}
                      </td>
                      <td className="px-3 py-2 capitalize">{row.paymentMethod}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {formatMoney(Number(row.total), currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex items-center justify-between gap-4">
              <p className="text-xs text-muted-foreground">
                Page {data.page} — {data.total} sale{data.total === 1 ? '' : 's'}
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page * data.limit >= data.total}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        ) : null}
      </Card>
    </div>
  )
}
